import { twMerge } from 'tailwind-merge';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Check, ChevronDown, ChevronRight, ChevronUp, Copy, Loader2, X } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { toast } from 'sonner';
import { cn } from '../lib/format';
import { useT } from '../lib/i18n';

// ---------------------------------------------------------------- Button

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type Size = 'xs' | 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-brand-600 text-white hover:bg-brand-700 shadow-sm shadow-brand-600/20 disabled:bg-brand-300',
  secondary:
    'bg-white text-zinc-800 border border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300 shadow-xs',
  outline: 'border border-zinc-300 text-zinc-700 hover:bg-zinc-100',
  ghost: 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900',
  danger: 'bg-red-600 text-white hover:bg-red-700 shadow-sm',
};
const SIZES: Record<Size, string> = {
  xs: 'h-7 px-2 text-xs gap-1 rounded-md',
  sm: 'h-8 px-3 text-sm gap-1.5 rounded-lg',
  md: 'h-9 px-4 text-sm gap-2 rounded-lg',
  lg: 'h-11 px-6 text-base gap-2 rounded-xl',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, className, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex shrink-0 items-center justify-center font-medium whitespace-nowrap transition-colors [&>svg]:shrink-0 focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" /> : icon}
      {children}
    </button>
  );
});

export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonProps & { label: string; active?: boolean }
>(function IconButton({ label, active, className, size = 'sm', variant = 'ghost', ...props }, ref) {
  // !px-0: the size classes add horizontal padding that would squeeze the icon to a few pixels
  const sq =
    size === 'xs'
      ? 'w-7 !px-0'
      : size === 'sm'
        ? 'w-8 !px-0'
        : size === 'md'
          ? 'w-9 !px-0'
          : 'w-11 !px-0';
  return (
    <Tooltip content={label}>
      <Button
        ref={ref}
        aria-label={label}
        variant={variant}
        size={size}
        className={cn(sq, active && 'bg-brand-50 text-brand-700 hover:bg-brand-100', className)}
        {...props}
      />
    </Tooltip>
  );
});

// ---------------------------------------------------------------- Form controls

/**
 * Classes of form fields: a width, height or padding passed by the caller replaces the default
 * (with plain concatenation `w-36` lost to the base `w-full`, depending on CSS order).
 */
const field = (...classes: Array<string | false | null | undefined>) => twMerge(cn(...classes));

const inputBase =
  'w-full rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-900 shadow-xs placeholder:text-zinc-400 transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:outline-none disabled:bg-zinc-50 disabled:text-zinc-500';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={field(inputBase, 'h-9', className)} {...props} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      className={field(inputBase, 'min-h-20 py-2 leading-relaxed', className)}
      {...props}
    />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...props }, ref) {
    return (
      <select
        ref={ref}
        className={field(
          inputBase,
          "h-9 appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 20 20%22 fill=%22%2371717a%22><path d=%22M5.5 7.5 10 12l4.5-4.5%22 stroke=%22%2371717a%22 stroke-width=%221.5%22 fill=%22none%22/></svg>')] bg-[length:16px] bg-[right_8px_center] bg-no-repeat pr-8",
          className,
        )}
        {...props}
      >
        {children}
      </select>
    );
  },
);

export function Label({
  children,
  className,
  htmlFor,
}: {
  children: ReactNode;
  className?: string;
  htmlFor?: string;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn('mb-1.5 block text-sm font-medium text-zinc-700', className)}
    >
      {children}
    </label>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      {label && <Label>{label}</Label>}
      {children}
      {error ? (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-zinc-500">{hint}</p>
      ) : null}
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  disabled,
  label,
  size = 'md',
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label?: ReactNode;
  size?: 'sm' | 'md';
}) {
  const sm = size === 'sm';
  return (
    <label
      className={cn(
        'inline-flex cursor-pointer items-center gap-2 select-none',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex shrink-0 rounded-full transition-colors',
          sm ? 'h-4 w-7' : 'h-5 w-9',
          checked ? 'bg-brand-600' : 'bg-zinc-300',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 rounded-full bg-white shadow transition-transform',
            sm ? 'size-3' : 'size-4',
            checked && (sm ? 'translate-x-3' : 'translate-x-4'),
          )}
        />
      </button>
      {label && <span className="text-sm text-zinc-700">{label}</span>}
    </label>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: ReactNode;
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-zinc-700 select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 rounded border-zinc-300 text-brand-600 accent-brand-600"
      />
      {label}
    </label>
  );
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  size = 'sm',
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: ReactNode; title?: string }>;
  size?: 'xs' | 'sm';
  className?: string;
}) {
  return (
    <div className={cn('inline-flex rounded-lg bg-zinc-100 p-0.5', className)}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          title={o.title}
          onClick={() => onChange(o.value)}
          className={cn(
            'flex flex-1 items-center justify-center gap-1 rounded-md font-medium whitespace-nowrap transition',
            size === 'xs' ? 'h-6 px-2 text-xs' : 'h-7 px-2.5 text-xs',
            value === o.value
              ? 'bg-white text-zinc-900 shadow-sm'
              : 'text-zinc-500 hover:text-zinc-800',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- Display

export function Badge({
  children,
  tone = 'zinc',
  className,
}: {
  children: ReactNode;
  tone?: 'zinc' | 'green' | 'red' | 'amber' | 'brand' | 'blue';
  className?: string;
}) {
  const tones = {
    zinc: 'bg-zinc-100 text-zinc-700',
    green: 'bg-emerald-50 text-emerald-700 ring-emerald-600/15',
    red: 'bg-red-50 text-red-700 ring-red-600/15',
    amber: 'bg-amber-50 text-amber-800 ring-amber-600/20',
    brand: 'bg-brand-50 text-brand-700 ring-brand-600/15',
    blue: 'bg-sky-50 text-sky-700 ring-sky-600/15',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-transparent ring-inset',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Card({
  children,
  className,
  ...rest
}: { children: ReactNode; className?: string } & Omit<HTMLAttributes<HTMLDivElement>, 'children'>) {
  return (
    <div
      className={cn('rounded-xl border border-zinc-200 bg-white shadow-xs', className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-5 animate-spin text-zinc-400', className)} />;
}

export function PageLoader() {
  return (
    <div className="flex h-full min-h-60 items-center justify-center">
      <Spinner className="size-6" />
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-zinc-300 bg-white px-6 py-14 text-center">
      {icon && <div className="mb-3 rounded-xl bg-brand-50 p-3 text-brand-600">{icon}</div>}
      <h3 className="text-base font-semibold text-zinc-900">{title}</h3>
      {description && <p className="mt-1 max-w-md text-sm text-zinc-500">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">{title}</h1>
        {description && <p className="mt-1 text-sm text-zinc-500">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Progress({
  value,
  className,
  tone = 'brand',
}: {
  value: number;
  className?: string;
  tone?: 'brand' | 'red' | 'amber';
}) {
  const color = tone === 'red' ? 'bg-red-500' : tone === 'amber' ? 'bg-amber-500' : 'bg-brand-600';
  return (
    <div className={cn('h-2 overflow-hidden rounded-full bg-zinc-100', className)}>
      <div
        className={cn('h-full rounded-full transition-all duration-500', color)}
        style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
      />
    </div>
  );
}

// ---------------------------------------------------------------- Copy / code

export async function copyText(text: string, message?: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    ta.remove();
  }
  toast.success(message ?? 'Copied');
}

export function CopyButton({
  text,
  label,
  size = 'xs',
}: {
  text: string;
  label?: string;
  size?: Size;
}) {
  const t = useT();
  const [done, setDone] = useState(false);
  return (
    <Button
      size={size}
      variant="ghost"
      icon={done ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      onClick={() => {
        void copyText(text, t('common.copied'));
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
    >
      {label}
    </Button>
  );
}

export function CodeBlock({
  code,
  className,
  language,
}: {
  code: string;
  className?: string;
  language?: string;
}) {
  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-xl bg-zinc-950 text-zinc-100',
        className,
      )}
    >
      {language && (
        <div className="border-b border-white/10 px-4 py-1.5 text-[11px] font-medium tracking-wide text-zinc-400 uppercase">
          {language}
        </div>
      )}
      <pre className="scrollbar-thin overflow-x-auto p-4 font-mono text-[12.5px] leading-relaxed">
        <code>{code}</code>
      </pre>
      <div className="absolute top-1 right-1 opacity-0 transition group-hover:opacity-100 [&_button]:text-zinc-300 [&_button:hover]:bg-white/10 [&_button:hover]:text-white">
        <CopyButton text={code} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Dialog

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  bodyClassName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Replaces the scrolling body wrapper (for dialogs that scroll parts of themselves). */
  bodyClassName?: string;
}) {
  const widths = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-6xl',
  };
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-zinc-950/40 backdrop-blur-[2px] data-[state=open]:animate-in" />
        <DialogPrimitive.Content
          className={cn(
            'fixed top-1/2 left-1/2 z-50 flex max-h-[90vh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-2xl bg-white shadow-2xl focus:outline-none',
            widths[size],
          )}
          onOpenAutoFocus={(e) => {
            const el = e.currentTarget as HTMLElement;
            const first = el.querySelector<HTMLElement>('input,textarea,select');
            if (first) {
              e.preventDefault();
              first.focus();
            }
          }}
        >
          {(title || description) && (
            <div className="flex items-start justify-between gap-4 border-b border-zinc-100 px-6 pt-5 pb-4">
              <div>
                {title && (
                  <DialogPrimitive.Title className="text-lg font-semibold text-zinc-900">
                    {title}
                  </DialogPrimitive.Title>
                )}
                {description && (
                  <DialogPrimitive.Description className="mt-1 text-sm text-zinc-500">
                    {description}
                  </DialogPrimitive.Description>
                )}
              </div>
              <DialogPrimitive.Close className="-mr-2 rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700">
                <X className="size-5" />
              </DialogPrimitive.Close>
            </div>
          )}
          {!title && <DialogPrimitive.Title className="sr-only">Dialog</DialogPrimitive.Title>}
          <div className={bodyClassName ?? 'scrollbar-thin flex-1 overflow-y-auto px-6 py-5'}>
            {children}
          </div>
          {footer && (
            <div className="flex items-center justify-end gap-2 border-t border-zinc-100 px-6 py-4">
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  danger = true,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => Promise<unknown> | void;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
                onOpenChange(false);
              } finally {
                setBusy(false);
              }
            }}
          >
            {confirmLabel ?? t('common.confirm')}
          </Button>
        </>
      }
    >
      {description && <p className="text-sm text-zinc-600">{description}</p>}
    </Dialog>
  );
}

// ---------------------------------------------------------------- Menu & tooltip

export function Menu({ modal = false, ...props }: DropdownPrimitive.DropdownMenuProps) {
  return <DropdownPrimitive.Root modal={modal} {...props} />;
}
export const MenuTrigger = DropdownPrimitive.Trigger;

export function MenuContent({
  children,
  align = 'end',
  side,
  className,
}: {
  children: ReactNode;
  align?: 'start' | 'end' | 'center';
  side?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content
        align={align}
        side={side}
        sideOffset={6}
        collisionPadding={8}
        className={cn(
          'z-50 min-w-48 rounded-xl border border-zinc-200 bg-white p-1 shadow-lg shadow-zinc-950/5',
          className,
        )}
      >
        {children}
      </DropdownPrimitive.Content>
    </DropdownPrimitive.Portal>
  );
}

/**
 * Right-click menu at the pointer, for things that also have a "⋯" menu (or none): the same
 * items, where people look for them in design tools. `onContextMenu` goes on the element,
 * `menu(items)` is rendered next to it.
 */
export function useContextMenu(onOpen?: () => void) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const onContextMenu = (e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onOpen?.();
    setAt({ x: e.clientX, y: e.clientY });
  };
  const menu = (items: ReactNode, className?: string) =>
    at ? (
      <Menu open onOpenChange={(o) => !o && setAt(null)}>
        <MenuTrigger asChild>
          <span className="fixed size-0" style={{ left: at.x, top: at.y }} />
        </MenuTrigger>
        <MenuContent align="start" side="bottom" className={className}>
          <div data-testid="context-menu">{items}</div>
        </MenuContent>
      </Menu>
    ) : null;
  return { onContextMenu, menu, open: at !== null };
}

export function MenuItem({
  children,
  onSelect,
  icon,
  danger,
  disabled,
  shortcut,
  'data-testid': testId,
}: {
  children: ReactNode;
  onSelect?: () => void;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  /** Keyboard shortcut shown on the right, e.g. "Ctrl+D". */
  shortcut?: string;
  'data-testid'?: string;
}) {
  return (
    <DropdownPrimitive.Item
      data-testid={testId}
      disabled={disabled}
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        danger
          ? 'text-red-600 data-[highlighted]:bg-red-50'
          : 'text-zinc-700 data-[highlighted]:bg-zinc-100',
      )}
    >
      {icon && <span className="text-zinc-400 [&>svg]:size-4">{icon}</span>}
      <span className="flex-1">{children}</span>
      {shortcut && <Kbd>{shortcut}</Kbd>}
    </DropdownPrimitive.Item>
  );
}

/** Keyboard shortcut badge. */
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="ml-4 rounded-md bg-zinc-100 px-1.5 py-0.5 font-sans text-[11px] font-medium text-zinc-500">
      {children}
    </kbd>
  );
}

export const MenuSub = DropdownPrimitive.Sub;

export function MenuSubTrigger({ children, icon }: { children: ReactNode; icon?: ReactNode }) {
  return (
    <DropdownPrimitive.SubTrigger className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm text-zinc-700 outline-none select-none data-[highlighted]:bg-zinc-100 data-[state=open]:bg-zinc-100">
      {icon && <span className="text-zinc-400 [&>svg]:size-4">{icon}</span>}
      <span className="flex-1">{children}</span>
      <ChevronRight className="size-4 text-zinc-400" />
    </DropdownPrimitive.SubTrigger>
  );
}

export function MenuSubContent({ children }: { children: ReactNode }) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.SubContent
        sideOffset={6}
        collisionPadding={8}
        className="z-50 min-w-48 rounded-xl border border-zinc-200 bg-white p-1 shadow-lg shadow-zinc-950/5"
      >
        {children}
      </DropdownPrimitive.SubContent>
    </DropdownPrimitive.Portal>
  );
}

export function MenuSeparator() {
  return <DropdownPrimitive.Separator className="my-1 h-px bg-zinc-100" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <DropdownPrimitive.Label className="px-2.5 py-1 text-xs font-medium text-zinc-400">
      {children}
    </DropdownPrimitive.Label>
  );
}

export function Tooltip({
  content,
  children,
  side = 'bottom',
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
}) {
  if (!content) return <>{children}</>;
  return (
    <TooltipPrimitive.Root delayDuration={350}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className="z-[60] rounded-md bg-zinc-900 px-2 py-1 text-xs text-white shadow"
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export const TooltipProvider = TooltipPrimitive.Provider;

// ---------------------------------------------------------------- Tabs (lightweight)

export function Tabs<T extends string>({
  value,
  onChange,
  tabs,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  tabs: Array<{ value: T; label: ReactNode; icon?: ReactNode }>;
  className?: string;
}) {
  return (
    <div className={cn('flex gap-1 border-b border-zinc-200', className)}>
      {tabs.map((tab) => (
        <button
          key={tab.value}
          onClick={() => onChange(tab.value)}
          className={cn(
            '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition',
            value === tab.value
              ? 'border-brand-600 text-brand-700'
              : 'border-transparent text-zinc-500 hover:text-zinc-800',
          )}
        >
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- Drop zone

export function FileDrop({
  accept,
  multiple,
  onFiles,
  children,
  className,
}: {
  accept?: string;
  multiple?: boolean;
  onFiles: (files: File[]) => void;
  children: ReactNode;
  className?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length) onFiles(multiple ? files : files.slice(0, 1));
      }}
      onClick={() => input.current?.click()}
      className={cn(
        'cursor-pointer rounded-xl border-2 border-dashed transition',
        over
          ? 'border-brand-500 bg-brand-50'
          : 'border-zinc-300 hover:border-brand-400 hover:bg-zinc-50',
        className,
      )}
    >
      <input
        ref={input}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) onFiles(files);
          e.target.value = '';
        }}
      />
      {children}
    </div>
  );
}

/** Debounced value helper for search inputs. */
export function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

// ---------------------------------------------------------------- number stepper

const roundStep = (v: number) => Math.round(v * 1000) / 1000;

/**
 * ▲▼ buttons for numeric fields. Click steps once (Shift ×10), holding repeats, and dragging
 * up/down while holding scrubs the value smoothly.
 */
export function Stepper({
  value,
  step = 1,
  onValue,
  className,
}: {
  value: number;
  step?: number;
  /** Receives the new (unclamped) value; the field clamps and commits it. */
  onValue: (v: number) => void;
  className?: string;
}) {
  const t = useT();
  const press = (dir: 1 | -1) => (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const base = value;
    const startY = e.clientY;
    let cur = roundStep(base + dir * step * (e.shiftKey ? 10 : 1));
    let dragging = false;
    onValue(cur);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const repeat = () => {
      cur = roundStep(cur + dir * step);
      onValue(cur);
      timer = setTimeout(repeat, 60);
    };
    timer = setTimeout(repeat, 400);
    const move = (ev: PointerEvent) => {
      const dy = startY - ev.clientY;
      if (!dragging && Math.abs(dy) < 4) return;
      if (!dragging) {
        dragging = true;
        clearTimeout(timer);
        document.body.style.cursor = 'ns-resize';
      }
      const n = Math.round(dy / 3) * step * (ev.shiftKey ? 10 : 1);
      const next = roundStep(base + n);
      if (next !== cur) {
        cur = next;
        onValue(cur);
      }
    };
    const up = () => {
      clearTimeout(timer);
      document.body.style.cursor = '';
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const btn =
    'flex flex-1 cursor-ns-resize items-center justify-center text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-700 active:bg-brand-50 active:text-brand-700';
  return (
    <div
      className={cn(
        'flex h-full w-4 shrink-0 flex-col self-stretch overflow-hidden border-l border-zinc-200 select-none',
        className,
      )}
      title={t('common.stepperHint')}
    >
      <button type="button" tabIndex={-1} aria-label="+" className={btn} onPointerDown={press(1)}>
        <ChevronUp className="size-2.5" strokeWidth={2.5} />
      </button>
      <button type="button" tabIndex={-1} aria-label="−" className={btn} onPointerDown={press(-1)}>
        <ChevronDown className="size-2.5" strokeWidth={2.5} />
      </button>
    </div>
  );
}

/** Text-style number input with steppers (native spinners are hidden app-wide). */
export function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  className,
  ...rest
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'min' | 'max' | 'step'>) {
  const [text, setText] = useState(String(value));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(String(value));
  }, [value]);
  const clamp = (v: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, v));
  const set = (v: number) => {
    const c = clamp(v);
    onChange(c);
    setText(String(c));
  };
  return (
    <div
      className={cn(
        'flex h-9 w-full overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-xs transition focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20',
        className,
      )}
    >
      <input
        {...rest}
        inputMode="decimal"
        className="h-full min-w-0 flex-1 bg-transparent px-3 text-sm text-zinc-900 tabular-nums outline-none"
        value={text}
        onFocus={(e) => {
          focused.current = true;
          e.target.select();
        }}
        onChange={(e) => {
          setText(e.target.value);
          const v = Number(e.target.value.replace(',', '.'));
          if (e.target.value.trim() !== '' && Number.isFinite(v)) onChange(v);
        }}
        onBlur={(e) => {
          focused.current = false;
          const v = Number(e.target.value.replace(',', '.'));
          set(Number.isFinite(v) && e.target.value.trim() !== '' ? v : value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            set(roundStep(value + (e.key === 'ArrowUp' ? 1 : -1) * step * (e.shiftKey ? 10 : 1)));
          }
        }}
      />
      <Stepper value={value} step={step} onValue={set} className="w-5" />
    </div>
  );
}
